import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StreakCounter from "../../../../components/StreakCounter";
import NextBlockPreview from "./NextBlockPreview";

const TILE_COLORS = {
  2: "#abc9eb",
  4: "#89aed8",
  8: "#6999cf",
  16: "#4c83c2",
  32: "#4ca6c2",
  64: "#59b8a7",
  128: "#82b879",
  256: "#b6bf73",
  512: "#baa5d9",
  1024: "#e0a580",
};

const GameHUD = ({
  score,
  bestScore,
  nextBlock,
  upcomingBlock,
  currentDate,
  streak,
  isStreakActive,
  isMuted,
  skipsAvailable,
  skipsUsed,
  isSkipAnimation,
  onHelp,
  onMute,
  onDateClick,
  onScoreInfo,
  onSkip,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    {/* Top row: Controls, Date, Streak */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1,
      }}
    >
      {/* Left: Help and Sound controls */}
      <Box sx={{ display: "flex", gap: 1 }}>
        <Tooltip title="How to Play">
          <IconButton onClick={onHelp} sx={{ color: "#222" }}>
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isMuted ? "Unmute" : "Mute"}>
          <IconButton onClick={onMute} sx={{ color: "#222" }}>
            {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>
        </Tooltip>
      </Box>
      {/* Center: Date */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "1.25rem",
          color: "#222",
          cursor: "pointer",
          textDecoration: "underline",
          transition: "text-decoration 0.2s",
          "&:hover": { textDecoration: "underline dotted" },
        }}
        onClick={onDateClick}
      >
        {currentDate.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}
      </Typography>
      {/* Right: Streak */}
      <Tooltip title="Keep playing daily to unlock special features!">
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <StreakCounter isActive={isStreakActive} streak={streak} />
        </Box>
      </Tooltip>
    </Box>
    {/* Bottom row: Score, NextBlockPreview, Best */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      {/* Left: Score */}
      <Tooltip title="Click to learn how scoring works!">
        <Chip
          label={`Score: ${score}`}
          sx={{
            bgcolor: "rgba(0, 0, 0, 0.1)",
            "& .MuiChip-label": { fontWeight: 600, color: "#000000" },
            cursor: "pointer",
          }}
          onClick={onScoreInfo}
        />
      </Tooltip>
      {/* Center: NextBlockPreview with Skip Button */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
        }}
      >
        <NextBlockPreview
          nextBlock={nextBlock}
          upcomingBlock={upcomingBlock}
          hideLabel
          isSkipAnimation={isSkipAnimation}
        />
        {/* Skip Button */}
        <Tooltip
          title={
            skipsAvailable === 0
              ? "Score 120+ points to unlock skips!"
              : skipsUsed >= skipsAvailable
              ? "No skips remaining"
              : "Skip this tile"
          }
        >
          <IconButton
            onClick={onSkip}
            disabled={skipsUsed >= skipsAvailable}
            sx={{
              color: skipsUsed >= skipsAvailable ? "#ccc" : "#666",
              "&:hover": {
                color: skipsUsed >= skipsAvailable ? "#ccc" : "#222",
              },
              transition: "color 0.2s",
              p: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: "inherit",
                  fontSize: "0.7rem",
                }}
              >
                x{Math.max(0, skipsAvailable - skipsUsed)}
              </Typography>
            </Box>
          </IconButton>
        </Tooltip>
      </Box>
      {/* Right: Best Score */}
      <Chip
        // icon={<EmojiEventsIcon sx={{ color: "#FFD700" }} />}
        label={`Best: ${bestScore !== null ? bestScore : "---"}`}
        sx={{
          bgcolor: "rgba(0, 0, 0, 0.1)",
          "& .MuiChip-label": { fontWeight: 600, color: "#222" },
        }}
      />
    </Box>
  </Box>
);

export default GameHUD;
